import { Component, output } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, Validators, FormGroup } from '@angular/forms';
import { InputLabel } from '../../parts/input-label/input-label.component';
import { TimezoneDropdown } from '../../parts/timezone-dropdown/timezone-dropdown';
import { CurrencyDropdown } from '../../parts/currency-dropdown/currency-dropdown';
import { ApiResponseUtilities } from '../../core/utilities/api-response.utilities';
import { OrganizationService } from '../../core/services/organization.service';
import { CreateOrganizationRequest } from '../../core/models/data/organization.model';
import { BillingStatuses, PricingTiers } from '../../core/enums/data.enums';
import { EnumUtilities } from '../../core/utilities/enum.utilities';
import { FormMask } from '../../core/enums/forms.enums';
import { CustomValidators } from '../../core/validators/custom-validators';
import { ServiceResultModel } from '../../core/models/service/service-result.model';
import { LocalizationConstants } from '../../core/constants/settings.constants';
import { BaseFormComponent } from '../../core/base-classes/base-form-component';

@Component({
  selector: 'ms-add-organization-form',
  imports: [InputLabel, ReactiveFormsModule, TimezoneDropdown, CurrencyDropdown],
  templateUrl: './add-organization.form.html',
})
export class AddOrganizationForm extends BaseFormComponent {
  public FormMask = FormMask;

  public addOrganizationForm: FormGroup;

  public submitSuccess: boolean | null = null;

  public cancelRequested = output();
  public submissionResult = output<ServiceResultModel>();

  constructor(private organizationService: OrganizationService, private fb: FormBuilder) {
    super();
    this.addOrganizationForm = this.fb.group({
      organizationName: ['', [Validators.required]],
      replyToEmailAddress: ['', [Validators.required, CustomValidators.email]],
      slug: ['', [Validators.required, CustomValidators.slug]],
      billingStatus: [BillingStatuses.Trial, [Validators.required]],
      pricingTier: [PricingTiers.Trial, [Validators.required]],
      timezone: [LocalizationConstants.DEFAULT_TIMEZONE_ID(), []],
      currency: [LocalizationConstants.DEFAULT_CURRENCY_ID(), []],
    });
  }

  public get billingStatusOptions(): Array<{ key: string; value: BillingStatuses; label: string }> {
    return Object.entries(BillingStatuses).map(([key, value]) => ({
      key,
      value,
      label: EnumUtilities.convertToLabel(key)
    }));
  }

  public get pricingTierOptions(): Array<{ key: string; value: PricingTiers; label: string }> {
    return Object.entries(PricingTiers).map(([key, value]) => ({
      key,
      value,
      label: EnumUtilities.convertToLabel(key)
    }));
  }

  public onCancel(): void {    
    this.cancelRequested.emit();
  }

  public async onSubmit(): Promise<void> {
    if (this.addOrganizationForm.invalid) {
      this.addOrganizationForm.markAllAsTouched();
    } else {
      this.isSubmitting = true;
      const timezoneValue = this.addOrganizationForm.value.timezone;
      const timeZoneId: string = typeof timezoneValue === 'string'
        ? timezoneValue
        : timezoneValue?.id;

      const currencyValue = this.addOrganizationForm.value.currency;
      const rawCurrencyId = typeof currencyValue === 'object' && currencyValue !== null
        ? currencyValue.currencyId
        : currencyValue;
      const parsedCurrencyId = Number(rawCurrencyId);
      const currencyId: number = Number.isFinite(parsedCurrencyId) && parsedCurrencyId > 0
        ? parsedCurrencyId
        : LocalizationConstants.DEFAULT_CURRENCY_ID();

      const createOrganization = {
        name: this.addOrganizationForm.value.organizationName,
        replyToEmailAddress: this.addOrganizationForm.value.replyToEmailAddress,
        slug: this.addOrganizationForm.value.slug,
        billingStatus: this.addOrganizationForm.value.billingStatus,
        pricingTier: this.addOrganizationForm.value.pricingTier,
        currency: currencyId,
        timeZoneId: timeZoneId,
      } as CreateOrganizationRequest;

      const submitResponse = await this.organizationService.createOrganization(createOrganization);

      if (submitResponse.success === false && ApiResponseUtilities.isClientErrorStatusCode(submitResponse.statusCode)) {
        this.submitSuccess = false;
        if (ApiResponseUtilities.isConflictStatusCode(submitResponse.statusCode)) {
          this.addOrganizationForm.controls['slug'].setErrors({ slug: { message: submitResponse.message } });
        }
      } else {
        this.resetForm();
        this.submitSuccess = true;
      }

      this.isSubmitting = false;
      this.submissionResult.emit(submitResponse);
    }
  }

  public resetForm(): void {
    this.addOrganizationForm.reset({
      organizationName: null,
      replyToEmailAddress: null,
      slug: null,
      billingStatus: BillingStatuses.Trial,
      pricingTier: PricingTiers.Trial,
      timezone: LocalizationConstants.DEFAULT_TIMEZONE_ID(),
      currency: LocalizationConstants.DEFAULT_CURRENCY_ID()
    });

    this.submitSuccess = null;
  }

  public getFormState(): any {
    return {
      formValue: this.addOrganizationForm.value,
      formValid: this.addOrganizationForm.valid,
      formTouched: this.addOrganizationForm.touched,
      formDirty: this.addOrganizationForm.dirty
    };
  }
}